// Painel de REGISTROS DE CLASSIFICAÇÃO do comitê (decisão do operador, 2026-08-10).
//
// A triagem de um caso é única, mas RECLASSIFICAR é legítimo — a gravidade muda
// quando aparece elemento novo. O `state` guarda só a classificação VIGENTE; o
// histórico completo de quem classificou para quê sempre viveu no audit
// hash-chained, e não tinha superfície própria: ficava diluído entre todos os
// outros eventos do `HistoricoPanel`.
//
// Este painel lê o MESMO endpoint estreito (`GET /:id/audit`, projeção
// `projectComite`) e filtra `triagem_classificada`. Não busca dado de lugar
// nenhum além do que o servidor já projetou — mesma regra do HistoricoPanel.
//
// ⚠️ Tempo: SEMANA, não hora. A projeção do comitê declara no próprio cabeçalho
// "Tempo = createdWeek (NUNCA epoch)" — a timeline por-caso é metadado-only.
// O evento JÁ carrega `createdAtEpochMs` no payload (ADR-0019 §7, ato de membro
// identificado), mas quem descarta é a projeção, deliberadamente. Mudar isso é
// decisão de quem escreveu aquela regra, não efeito colateral deste painel.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Thead, Tbody, Tr, Th, Td, Alert, Card, dataHora } from '@aions/ui-primitives';
import { apiFetch } from '../lib/auth-client';

interface Props {
  caseId: string;
  /** id→e-mail para nomear quem classificou. Ausente → mostra o id cru. */
  members?: ReadonlyArray<{ id: string; email: string }> | null;
}

/** Data e hora do ato; semana quando a entrada é anterior à mudança de 13/08. */
function quandoClass(e: { createdAtEpochMs?: number; createdWeek: string }): string {
  return dataHora(e.createdAtEpochMs, e.createdWeek, { segundos: true });
}

interface TimelineEvent {
  seq: number;
  createdWeek: string;
  /** Data e hora do ato (decisão do operador 2026-08-13). Mesmo endpoint `/audit` do
   *  HistoricoPanel, que já projetava o campo — este painel não o lia. */
  createdAtEpochMs?: number;
  eventType?: string;
  userId?: string;
  gravidade?: string;
  rota?: string;
}

export function ClassificacoesPanel({ caseId, members }: Props): JSX.Element {
  const { t } = useTranslation();
  const [events, setEvents] = useState<TimelineEvent[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(false);
    setEvents(null);
    void (async () => {
      try {
        const res = await apiFetch(`/api/comite/cases/${caseId}/audit`);
        if (cancelled) return;
        if (!res.ok) {
          setError(true);
          return;
        }
        const body = (await res.json()) as { events: TimelineEvent[] };
        if (!cancelled) setEvents(body.events);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  if (error) {
    return <Alert variant="error">{t('classificacoes.load_error')}</Alert>;
  }
  if (events === null) {
    return <p>{t('common.loading')}</p>;
  }

  // `seq` é a ordem da cadeia (bigserial / ordem de append) — a ordenação real da
  // trilha. NÃO ordenar por ulid: ele é opaco desde o LT-01 e ordenar por ele
  // embaralharia em silêncio.
  const classificacoes = events
    .filter((e) => e.eventType === 'triagem_classificada')
    .sort((a, b) => a.seq - b.seq);

  const emailPorId = new Map((members ?? []).map((m) => [m.id, m.email]));

  return (
    <Card>
      <h3>{t('classificacoes.titulo')}</h3>
      <p className="classificacoes__ajuda">{t('classificacoes.ajuda')}</p>

      {classificacoes.length === 0 ? (
        <p>{t('classificacoes.vazio')}</p>
      ) : (
        <Table>
          <Thead>
            <Tr>
              <Th>{t('classificacoes.col_ordem')}</Th>
              <Th>{t('classificacoes.col_gravidade')}</Th>
              <Th>{t('classificacoes.col_rota')}</Th>
              <Th>{t('classificacoes.col_por')}</Th>
              <Th>{t('classificacoes.col_quando')}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {classificacoes.map((e, i) => (
              <Tr key={e.seq}>
                {/* A PRIMEIRA é a classificação inicial; as demais são
                    reclassificações. Rotular pela posição — e não por um campo
                    do payload — é o que mantém o painel correto sem depender de
                    o servidor marcar cada evento. */}
                <Td>
                  {i === 0 ? t('classificacoes.inicial') : t('classificacoes.reclassificacao')}
                </Td>
                <Td>{e.gravidade ?? '-'}</Td>
                <Td>{e.rota ?? '-'}</Td>
                {/* E-mail quando conhecido; id cru quando não — nunca "-" no
                    lugar de um id que existe (mesmo padrão da AuditPage). */}
                <Td mono title={e.userId ?? undefined}>
                  {e.userId ? (emailPorId.get(e.userId) ?? e.userId) : '-'}
                </Td>
                {/* Data e hora, com a SEMANA como fallback para entradas gravadas
                    antes da mudança de 13/08 — elas realmente não têm hora. */}
                <Td>{quandoClass(e)}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
    </Card>
  );
}
